/**
 * Merge the current shopper into a page that was rendered for everyone.
 */

import { showToast } from './toast.js';

function getConfig() {
	return window.doneCommerce || {};
}

function whenDomReady() {
	if (document.readyState !== 'loading') {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		document.addEventListener('DOMContentLoaded', resolve, { once: true });
	});
}

function applySession(state) {
	const config = getConfig();

	if (typeof state.nonce === 'string' && state.nonce) {
		config.nonce = state.nonce;
	}
	if (typeof state.isLoggedIn === 'boolean') {
		config.isLoggedIn = state.isLoggedIn;
	}
	if (typeof state.firstName === 'string') {
		config.firstName = state.firstName;
	}

	window.doneCommerce = config;
}

function applyDom(state) {
	const cartCount = Number(state.cartCount);
	if (Number.isFinite(cartCount)) {
		document.querySelectorAll('[data-cart-count]').forEach((el) => {
			el.textContent = cartCount > 0 ? String(cartCount) : '';
			el.dataset.count = String(cartCount);
			el.hidden = cartCount <= 0;
		});
	}

	const favouriteCount = Number(state.favouriteCount);
	if (Number.isFinite(favouriteCount)) {
		document.querySelectorAll('[data-favourites-count]').forEach((el) => {
			el.textContent = favouriteCount > 0 ? String(favouriteCount) : '';
			el.hidden = favouriteCount <= 0;
			el.setAttribute('data-count', String(favouriteCount));
		});
	}

	if (state.isLoggedIn) {
		promoteFavouritesLink();
	}

	if (!Array.isArray(state.notices)) {
		return;
	}

	state.notices.forEach((item) => {
		const message = String(item?.message || '').trim();
		if (!message) {
			return;
		}
		showToast(message, { variant: item.variant === 'error' ? 'error' : 'success' });
	});
}

function promoteFavouritesLink() {
	const config = getConfig();
	const href = config.favouritesUrl || config.accountUrl;
	if (!href) {
		return;
	}

	document.querySelectorAll('[data-favourites-toggle]').forEach((button) => {
		const link = document.createElement('a');
		link.href = href;
		link.className = button.className;
		link.setAttribute('data-favourites-link', '');
		link.setAttribute('aria-label', button.getAttribute('aria-label') || 'Favourites');
		link.innerHTML = button.innerHTML;
		button.replaceWith(link);
	});
}

async function fetchState() {
	const config = getConfig();
	const url = new URL(config.homeUrl || '/', window.location.origin);
	url.searchParams.set('dc_storefront', '1');

	const response = await fetch(url.toString(), {
		credentials: 'same-origin',
		cache: 'no-store',
		headers: { Accept: 'application/json' },
	});

	if (!response.ok) {
		throw new Error('storefront state');
	}

	return response.json();
}

/**
 * Resolves once nonce, login, and counts match this shopper.
 * Safe to await more than once.
 */
export const storefrontReady = (async () => {
	let state = window.dcStorefrontState || null;

	if (!state && getConfig().pageCache) {
		try {
			state = await fetchState();
		} catch {
			state = null;
		}
	}

	if (!state) {
		return;
	}

	applySession(state);
	await whenDomReady();
	applyDom(state);
})();
